import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import {
  Check, Sparkles, Zap, Building2, ArrowRight, Loader2,
  CreditCard, ShieldCheck, Lock, Crown, Rocket, Star, X,
  Handshake, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import FooterBackground from '../components/FooterBackground';
import PublicNavbar from '../components/PublicNavbar';
import { ResumeCheckoutBanner } from '../components/ResumeCheckoutBanner';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────────
// Pricing tiers — calibrated to sit ~15-30% below the median competitor mid-
// market price (Vantage starts at $1,200/mo paid, Apptio Cloudability ~$3,000/mo,
// Anodot ~$20K/yr custom). We position as the "premium challenger" — credible
// pricing that signals seriousness while still beating incumbents on TCO.
// ─────────────────────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'trial',
    name: 'Free Trial',
    price: 0,
    cycle: '',
    priceLabel: 'Free for 14 days',
    cta: 'Start 14-day Trial',
    icon: Sparkles,
    iconColor: 'text-slate-300',
    highlight: false,
    badge: 'NO CARD REQUIRED',
    features: [
      { text: 'Full Pro features for 14 days', included: true },
      { text: '1 cloud account', included: true },
      { text: 'AI Advisor & cost forecasting', included: true },
      { text: 'Email alerts', included: true },
      { text: 'Auto-converts to Pro after trial', included: true },
      { text: 'Cancel anytime, no card on file', included: true },
    ],
  },
  {
    id: 'pro_monthly',
    name: 'Pro',
    price: 199,
    cycle: '/month',
    altPriceId: 'pro_annual',
    altPriceLabel: 'or $1,791/yr — save $597 (25% off)',
    cta: 'Start Pro Monthly',
    icon: Rocket,
    iconColor: 'text-emerald-400',
    highlight: true,
    badge: 'MOST POPULAR · AI + CLOUD',
    features: [
      { text: 'Auto-Compiler Proxy — 20-50% AI token cut on typical prompts (up to 70% on repetitive workloads)', included: true },
      { text: 'Any model: OpenAI · Anthropic · Gemini · Groq · Mistral', included: true },
      { text: 'Up to 5 team members', included: true },
      { text: '3 cloud accounts (AWS · Azure · GCP)', included: true },
      { text: 'Snowflake + Databricks + DigitalOcean', included: true },
      { text: 'Unlimited AI Advisor queries', included: true },
      { text: '90-day cost history + 30-day forecast', included: true },
      { text: 'Slack / Teams / Webhook alerts', included: true },
      { text: 'FOCUS 1.1 export', included: true },
      { text: 'Public share-links for stakeholders', included: true },
      { text: 'Priority email support', included: true },
    ],
  },
  {
    id: 'business_monthly',
    name: 'Business',
    price: 999,
    cycle: '/month',
    altPriceId: 'business_annual',
    altPriceLabel: 'or $8,991/yr — save $2,997 (25% off)',
    cta: 'Start Business Monthly',
    icon: Building2,
    iconColor: 'text-emerald-400',
    highlight: false,
    badge: 'GROWING TEAMS',
    features: [
      { text: 'Everything in Pro — Auto-Compiler Proxy + multi-model routing', included: true },
      { text: 'Up to 50 team members', included: true },
      { text: 'Unlimited cloud accounts', included: true },
      { text: 'All 6 LIVE cloud integrations', included: true },
      { text: '12-month cost history + 90-day forecast', included: true },
      { text: 'Custom dashboards + saved views', included: true },
      { text: 'Advanced anomaly detection (auto-resolve)', included: true },
      { text: 'Webhook fan-out for incident broadcasting', included: true },
      { text: 'API access (5K calls/day)', included: true },
      { text: 'SSO-lite (Google + Microsoft)', included: true },
      { text: 'Priority Slack support — same-day response', included: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: null,
    priceLabel: 'Custom',
    cta: 'Contact Sales',
    icon: Crown,
    iconColor: 'text-emerald-400',
    highlight: false,
    badge: 'BANKS · F500',
    features: [
      { text: 'Unlimited team members + clouds', included: true },
      { text: 'Full SSO/SAML (Okta · Entra · OIDC)', included: true },
      { text: 'SOC 2 Type II · FedRAMP-ready · ISO 27001 controls', included: true },
      { text: 'Custom data-retention policies (1-7 years)', included: true },
      { text: 'Dedicated Customer Success Manager', included: true },
      { text: 'Uptime SLA — 99.9%', included: true },
      { text: 'On-prem / VPC deployment option', included: true },
      { text: 'SCIM + per-cost-center scoped roles', included: true },
      { text: 'Custom integrations + dedicated webhooks', included: true },
      { text: 'API access (unlimited, no rate cap)', included: true },
      { text: 'Compliance Mapper (SOC2 · ISO · HIPAA · FedRAMP)', included: true },
      { text: 'Quarterly business review with leadership', included: true },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [verifyingSession, setVerifyingSession] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  // Contextual upgrade banner — fires when redirected here from a quota paywall.
  // Pre-select Annual for paywall arrivals (higher conversion + bigger ACV).
  const fromTokenIntel = searchParams.get('from') === 'token-intel';
  const lockedFeature = searchParams.get('feature') || '';
  const [billingCycle, setBillingCycle] = useState(fromTokenIntel ? 'annual' : 'monthly');
  const [showContactSales, setShowContactSales] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '', email: '', company: '', cloudSpend: '', message: ''
  });
  const [contactSending, setContactSending] = useState(false);

  const FEATURE_LABELS = {
    'multi-model-router': 'Multi-Model Smart Router',
    'context-optimizer': 'Context Window Optimizer',
    'retry-cost-audit': 'Retry Cost Audit',
    'token-drift': 'Token Drift Alerter',
    'streaming-batch-diff': 'Streaming-vs-Batch Cost Diff',
  };
  const lockedFeatureLabel = FEATURE_LABELS[lockedFeature] || 'this feature';

  // Handle return-from-Stripe
  const sessionId = searchParams.get('session_id');
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;
    setVerifyingSession(true);
    const poll = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!cancelled) {
          setCheckoutResult({ status: 'error', message: 'Please sign in to complete checkout' });
          setVerifyingSession(false);
        }
        return;
      }
      try {
        const r = await axios.get(`${API}/billing/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        if (r.data.payment_status === 'paid') {
          setCheckoutResult({ status: 'paid', plan: r.data.plan, amount: r.data.amount_total });
          setVerifyingSession(false);
          toast.success('Payment successful — welcome aboard!');
        } else if (r.data.status === 'expired') {
          setCheckoutResult({ status: 'expired' });
          setVerifyingSession(false);
        } else {
          attempts++;
          if (attempts >= maxAttempts) {
            setCheckoutResult({ status: 'pending', message: 'Still verifying — refresh in a moment.' });
            setVerifyingSession(false);
          } else {
            setTimeout(poll, 2000);
          }
        }
      } catch {
        if (!cancelled) {
          setCheckoutResult({ status: 'error', message: 'Could not verify payment status' });
          setVerifyingSession(false);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Resolve which plan id to send to Stripe based on monthly/annual toggle
  const resolveCheckoutId = (plan) => {
    if (plan.id === 'trial') return 'trial';
    if (plan.id === 'enterprise') return 'enterprise';
    if (billingCycle === 'annual' && plan.altPriceId) return plan.altPriceId;
    return plan.id;
  };

  const startCheckout = async (plan) => {
    if (plan.id === 'enterprise') {
      setShowContactSales(true);
      return;
    }
    const token = localStorage.getItem('token');
    if (plan.id === 'trial') {
      // Trial = signup flow (no payment). Auto-grants 14-day Pro on register.
      sessionStorage.setItem('signup_intent', 'trial');
      navigate(token ? '/dashboard' : '/login?signup=1');
      return;
    }
    if (!token) {
      sessionStorage.setItem('post_login_redirect', location.pathname + location.search);
      navigate('/login');
      return;
    }
    const checkoutId = resolveCheckoutId(plan);
    setLoadingPlan(plan.id);
    try {
      const origin = window.location.origin;
      const r = await axios.post(
        `${API}/billing/checkout`,
        {
          plan: checkoutId,
          success_url: `${origin}/pricing?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/pricing?cancelled=1`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.url) window.location.href = r.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
      setLoadingPlan(null);
    }
  };

  const submitContactSales = async (e) => {
    e.preventDefault();
    setContactSending(true);
    try {
      await axios.post(`${API}/contact/sales`, contactForm).catch(() => {});
      toast.success('Thank you — our enterprise team will reach out within 1 business day.');
      setShowContactSales(false);
      setContactForm({ name: '', email: '', company: '', cloudSpend: '', message: '' });
    } finally {
      setContactSending(false);
    }
  };

  // Verifying / result banners
  if (verifyingSession) {
    return (
      <div className="dark min-h-screen bg-slate-950 text-slate-100 relative">
        <FooterBackground />
        <div className="relative max-w-md mx-auto pt-32 px-4 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-emerald-400 animate-spin" />
          <h1 className="text-2xl font-bold mb-2">Verifying your payment…</h1>
          <p className="text-slate-400">This usually takes 2-3 seconds.</p>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden" data-testid="pricing-page">
      <SEOHead
        title="Pricing — CARTIEAI · Pay 60% less for AI tokens"
        description="Pay 60% less for AI tokens with our drop-in Auto-Compiler Proxy. Any model — OpenAI, Anthropic, Gemini, Groq, Mistral. Plus Cloud FinOps for AWS/Azure/GCP. Backed by a 3× ROI pledge — full refund if we miss."
        canonical="https://cartieai.com/pricing"
        ogSlug="pricing"
        keywords="CARTIEAI pricing, AI token savings, LLM proxy pricing, FinOps pricing, OpenAI proxy cost"
      />
      <PublicNavbar />

      {/* Brand atmosphere orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-20 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/30 blur-[120px]" />
        <div className="absolute top-40 right-1/4 w-[380px] h-[380px] rounded-full bg-emerald-200/30 blur-[120px]" />
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[260px] h-[260px] rounded-full bg-teal-200/20 blur-[100px]" />
      </div>

      <div className="relative">
        <FooterBackground />
        <div className="relative max-w-7xl mx-auto px-4 pt-24 pb-16">
          {/* Abandoned-checkout recovery — resume an open Stripe session */}
          <ResumeCheckoutBanner />

          {/* Contextual paywall banner — only when arriving from a Token Intel quota hit */}
          {fromTokenIntel && (
            <div className="max-w-3xl mx-auto mb-8 bg-emerald-50 bg-gradient-to-r from-emerald-50 to-emerald-50 border-2 border-emerald-300 rounded-xl px-5 py-4 flex items-start gap-3 shadow-md shadow-emerald-200/40" data-testid="pricing-quota-banner">
              <span className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0 animate-pulse" />
              <div className="flex-1">
                <p className="text-emerald-900 text-sm font-semibold">You've hit your free limit on <span className="text-emerald-950">{lockedFeatureLabel}</span>.</p>
                <p className="text-slate-700 text-sm mt-1">Upgrade to <span className="font-semibold text-emerald-700">CARTIE Pro</span> below to unlock unlimited scans across all 10 Token Intelligence features.</p>
                <p className="text-[11px] text-emerald-700 mt-2 font-mono">→ Annual billing pre-selected · save 25% vs monthly</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-amber-300 mb-6 text-[11px] font-mono uppercase tracking-[0.18em] shadow-lg shadow-slate-900/20 border border-slate-800">
              <Sparkles className="w-3.5 h-3.5" /> PRICING · LEAD WITH AI TOKENS · MOAT IN THE CLOUD
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight mb-5 leading-[1.05]">
              <span className="text-white">Pay </span>
              <span className="text-emerald-400">60% less</span>
              <span className="text-white"> for AI tokens.</span>
              <span className="block text-white text-3xl sm:text-4xl lg:text-5xl mt-3 font-bold">
                Plus a Cloud FinOps moat for the next 32%.
              </span>
            </h1>
            <p className="text-slate-300 max-w-2xl mx-auto text-lg leading-relaxed font-medium">
              One drop-in proxy. Any model — OpenAI, Anthropic, Gemini, Groq, Mistral.
              <span className="text-white font-semibold"> 14-day free trial. No card. Cancel anytime.</span>
            </p>
            <div className="mt-5">
              <Link
                to="/try"
                className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 text-sm font-semibold underline underline-offset-4 decoration-emerald-500/40 hover:decoration-emerald-300"
                data-testid="pricing-try-demo-link"
              >
                <Zap className="w-4 h-4" />
                See your tokens drop 60% on your own prompt — 60-sec live demo
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* ROI Pledge banner — the contractual moat. Placed BEFORE tier selection
              so it frames every price the user sees through "this is reversible". */}
          <Link
            to="/roi-pledge"
            className="block max-w-3xl mx-auto mb-10 group"
            data-testid="pricing-roi-pledge-banner"
          >
            <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-slate-900/60 px-6 py-5 hover:border-emerald-400/60 hover:shadow-xl hover:shadow-emerald-500/10 transition-all hover:-translate-y-0.5 shadow-md shadow-slate-900/40">
              {/* Soft emerald glow */}
              <div className="absolute top-1/2 right-8 -translate-y-1/2 w-32 h-32 rounded-full bg-emerald-500/15 blur-3xl pointer-events-none" />
              <div className="relative flex items-center gap-5">
                <div className="shrink-0 inline-flex w-12 h-12 rounded-xl bg-emerald-500 items-center justify-center shadow-md shadow-emerald-500/30">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold tracking-widest text-emerald-300 uppercase mb-0.5">Backed by our binding pledge</div>
                  <p className="text-base sm:text-lg font-bold text-white leading-tight">
                    3× return on your subscription in 90 days — or a <span className="text-emerald-300">full refund</span>.
                  </p>
                  <p className="text-xs text-slate-400 mt-1">No email chains. No "we tried our best." A contract. Read it before you pay.</p>
                </div>
                <div className="shrink-0 text-emerald-400 group-hover:translate-x-1 transition-transform">
                  <ChevronRight className="w-5 h-5" />
                </div>
              </div>
            </div>
          </Link>

          {/* Monthly / Annual toggle */}
          <div className="flex justify-center mb-8">
            <div
              className="inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1"
              data-testid="billing-cycle-toggle"
            >
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid="cycle-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                  billingCycle === 'annual'
                    ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid="cycle-annual-btn"
              >
                Annual
                <span className="text-[11px] bg-emerald-400/20 text-emerald-300 px-1.5 py-0.5 rounded-full font-bold">
                  SAVE 25%
                </span>
              </button>
            </div>
          </div>

          {/* Result banner */}
          {checkoutResult?.status === 'paid' && (
            <Card className="bg-emerald-500/10 border-emerald-500/30 mb-8" data-testid="checkout-success-banner">
              <CardContent className="p-5 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-emerald-400 shrink-0" />
                <div className="flex-1">
                  <div className="font-bold">Payment successful — welcome aboard!</div>
                  <div className="text-sm text-slate-400">Your organization has been upgraded. Refresh /organization to see the new plan.</div>
                </div>
                <Button onClick={() => navigate('/organization')} variant="outline" className="border-white/20" data-testid="view-org-btn">
                  View Organization
                </Button>
              </CardContent>
            </Card>
          )}
          {checkoutResult?.status === 'expired' && (
            <Card className="bg-amber-500/10 border-amber-500/30 mb-8">
              <CardContent className="p-5 text-sm text-amber-300">
                That checkout session expired. Pick a plan below to start again.
              </CardContent>
            </Card>
          )}
          {checkoutResult?.status === 'error' && (
            <Card className="bg-rose-500/10 border-rose-500/30 mb-8">
              <CardContent className="p-5 text-sm text-rose-300">
                {checkoutResult.message}
              </CardContent>
            </Card>
          )}

          {/* Plans Grid (4 columns on desktop, 1 on mobile) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const showAnnualPrice = billingCycle === 'annual' && plan.altPriceId;
              // Map annual prices for display (Pro $1,990/yr ≈ $166/mo equivalent; Business $9,990/yr ≈ $832/mo)
              const annualMap = { pro_annual: 1791, business_annual: 8991 };
              const annualMonthlyEq = { pro_annual: 149, business_annual: 749 };
              const displayPrice = showAnnualPrice ? annualMap[plan.altPriceId] : plan.price;
              const displayCycle = showAnnualPrice ? '/year' : plan.cycle;

              return (
                <Card
                  key={plan.id}
                  className={`relative overflow-hidden transition-all ${
                    plan.highlight
                      ? 'bg-gradient-to-br from-emerald-500/[0.08] to-teal-500/[0.06] border-emerald-500/40 shadow-2xl shadow-emerald-500/10 lg:scale-[1.03]'
                      : 'bg-slate-900/60 border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`plan-${plan.id}`}
                >
                  {plan.badge && (
                    <div className="absolute top-3 right-3 z-10">
                      <Badge
                        className={`text-[11px] font-bold tracking-widest ${
                          plan.highlight
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-900 text-white border-slate-700'
                        }`}
                      >
                        {plan.badge}
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-7 pt-12">
                    {/* Plan name + icon */}
                    <div className="flex items-center gap-2 mb-3 text-white">
                      {Icon && <Icon className={`w-5 h-5 ${plan.iconColor || 'text-emerald-400'}`} />}
                      <span className="font-bold text-xl text-white">{plan.name}</span>
                    </div>

                    {/* Price */}
                    <div className="mb-6 min-h-[64px]">
                      {plan.id === 'enterprise' ? (
                        <div className="flex items-end gap-1">
                          <span className="text-3xl font-bold text-white">Custom</span>
                        </div>
                      ) : plan.id === 'trial' ? (
                        <div className="flex items-end gap-1">
                          <span className="text-2xl font-bold text-white">{plan.priceLabel}</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-end gap-1">
                            <span className="text-4xl font-bold text-white">${displayPrice.toLocaleString()}</span>
                            <span className="text-slate-400 mb-1">{displayCycle}</span>
                          </div>
                          {showAnnualPrice && (
                            <div className="text-xs text-emerald-400 mt-1">
                              ${annualMonthlyEq[plan.altPriceId]}/mo equivalent
                            </div>
                          )}
                          {!showAnnualPrice && plan.altPriceLabel && (
                            <div className="text-xs text-slate-400 mt-1">{plan.altPriceLabel}</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="space-y-2.5 mb-6 min-h-[280px]">
                      {plan.features.map((f, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          {f.included ? (
                            <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                          ) : (
                            <X className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                          )}
                          <span className={f.included ? 'text-slate-700' : 'text-slate-400 line-through'}>
                            {f.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Button
                      onClick={() => startCheckout(plan)}
                      disabled={loadingPlan === plan.id}
                      className={`w-full h-11 ${
                        plan.highlight
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : plan.id === 'enterprise'
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                      }`}
                      data-testid={`checkout-${plan.id}-btn`}
                    >
                      {loadingPlan === plan.id ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Redirecting…</>
                      ) : (
                        <>
                          {plan.id !== 'trial' && plan.id !== 'enterprise' && <CreditCard className="w-4 h-4 mr-1.5" />}
                          {/* Make CTA label reflect billing cycle for Pro/Business tiers. */}
                          {(plan.id === 'pro_monthly' || plan.id === 'business_monthly')
                            ? plan.cta.replace('Monthly', billingCycle === 'annual' ? 'Annual' : 'Monthly')
                            : plan.cta}
                          <ArrowRight className="w-4 h-4 ml-1.5" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Trust strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-12 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10 text-sm">
              <Lock className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-slate-400">Stripe secure checkout — we never see your card</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10 text-sm">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-slate-400">Cancel from your dashboard any time</span>
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10 text-sm">
              <CreditCard className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-slate-400">No setup fees · no contracts</span>
            </div>
          </div>

          {/* Competitor comparison — sharper visual contrast, savings math front-and-center */}
          <div className="mt-16 max-w-5xl mx-auto" data-testid="competitor-comparison-section">
            <div className="text-center mb-8">
              <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-mono tracking-widest mb-3">SIDE-BY-SIDE MATH</Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">Why pay <span className="text-amber-300">5×–15× more</span> for the same FinOps features?</h2>
              <p className="text-sm text-slate-400 mt-2">Public pricing from each vendor's site. We're the same category, priced for builders.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Vantage */}
              <Card className="bg-slate-900 bg-gradient-to-br from-slate-900 to-slate-950 border border-emerald-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-3">vs Vantage</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-extrabold text-slate-500 line-through">$1,200</span>
                    <span className="text-xs text-slate-500">/mo</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-extrabold text-emerald-300">$199</span>
                    <span className="text-xs text-emerald-400 font-semibold">/mo · CARTIE Pro</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold tracking-wider uppercase mb-3">You save $1,001/mo</div>
                  <p className="text-xs text-slate-400 leading-relaxed">Same multi-cloud P&L. Same anomaly detection. We add 6 LIVE clouds (they have 5) + ROI Pledge + Algorithm Correctness Manifest.</p>
                </CardContent>
              </Card>

              {/* Apptio Cloudability */}
              <Card className="bg-slate-900 bg-gradient-to-br from-slate-900 to-slate-950 border border-emerald-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-3">vs Apptio Cloudability</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-extrabold text-slate-500 line-through">$3,000</span>
                    <span className="text-xs text-slate-500">/mo</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-extrabold text-emerald-300">$999</span>
                    <span className="text-xs text-emerald-400 font-semibold">/mo · CARTIE Business</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold tracking-wider uppercase mb-3">You save $2,001/mo</div>
                  <p className="text-xs text-slate-400 leading-relaxed">Same custom dashboards. Same anomaly auto-resolve. Same SSO-lite. Plus engineer-hour-aware Payback Filter.</p>
                </CardContent>
              </Card>

              {/* CloudHealth */}
              <Card className="bg-slate-900 bg-gradient-to-br from-slate-900 to-slate-950 border border-amber-500/30 overflow-hidden">
                <CardContent className="p-6">
                  <div className="text-[10px] font-bold text-amber-400 uppercase tracking-widest mb-3">vs CloudHealth (VMware)</div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-extrabold text-slate-500 line-through">$30K+</span>
                    <span className="text-xs text-slate-500">/yr</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-3xl font-extrabold text-amber-300">Custom</span>
                    <span className="text-xs text-amber-400 font-semibold">CARTIE Enterprise</span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-bold tracking-wider uppercase mb-3">Transparent quotes</div>
                  <p className="text-xs text-slate-400 leading-relaxed">Matches CloudHealth on SOC 2-aligned controls + SSO/SAML + on-prem option. No "call us for a quote" runaround.</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Add-on note */}
          <div className="text-center mt-12 text-sm text-slate-400">
            <p>Need extra seats on Pro? Add additional members at <strong className="text-white">$19/seat/month</strong> from your Organization page.</p>
            <p className="mt-1 text-xs text-slate-400">Questions? <a href="mailto:hello@cartieai.com" className="text-emerald-400 hover:text-emerald-300">hello@cartieai.com</a></p>
          </div>
        </div>
      </div>

      {/* Contact Sales modal */}
      {showContactSales && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="contact-sales-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setShowContactSales(false); }}
        >
          <Card className="w-full max-w-lg bg-slate-900 border-white/10">
            <CardContent className="p-7">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest mb-2">
                    ENTERPRISE INQUIRY
                  </Badge>
                  <h3 className="text-2xl font-bold">Talk to our enterprise team</h3>
                  <p className="text-slate-400 text-sm mt-1">We'll respond within 1 business day with a custom quote.</p>
                </div>
                <button
                  onClick={() => setShowContactSales(false)}
                  className="text-slate-400 hover:text-white p-1"
                  data-testid="close-contact-sales-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={submitContactSales} className="space-y-3">
                <input
                  required value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  placeholder="Full name"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                  data-testid="contact-sales-name-input"
                />
                <input
                  required type="email" value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  placeholder="Work email"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                  data-testid="contact-sales-email-input"
                />
                <input
                  required value={contactForm.company}
                  onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                  placeholder="Company"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                  data-testid="contact-sales-company-input"
                />
                <select
                  required value={contactForm.cloudSpend}
                  onChange={(e) => setContactForm({ ...contactForm, cloudSpend: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-slate-900 focus:border-emerald-500 focus:outline-none"
                  data-testid="contact-sales-spend-select"
                >
                  <option value="">Monthly cloud spend</option>
                  <option value="<50k">Less than $50K</option>
                  <option value="50k-250k">$50K – $250K</option>
                  <option value="250k-1m">$250K – $1M</option>
                  <option value="1m+">$1M+</option>
                </select>
                <textarea
                  rows={3} value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  placeholder="What's the most important thing for us to understand?"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none resize-none"
                  data-testid="contact-sales-message-input"
                />
                <Button
                  type="submit"
                  disabled={contactSending}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="contact-sales-submit-btn"
                >
                  {contactSending ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</>
                  ) : (
                    <>Request a quote<ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
